import { createCharacter } from './builder';
import { createAttackAnimation, createIdleAnimation, createJumpAnimation, createRunAnimation } from './animations';
import { createMeleeHorizontalAttack, createRangedProjectileAttack } from './attacks';
import { RUN_SPEED, UPPER_BODY_MASK } from './constants';
import { createBasicLifecycleScript } from '../lifecycle';
import { fromFXPreset } from '../../systems/effects/FXPresets';

const DANIEL_THROW_ANIM_START_OFFSET = 0.5;
const DANIEL_PROJECTILE_HANDOVER_TIME = 0.55;

const DANIEL_MELEE_HORIZONTAL_ATTACK = createMeleeHorizontalAttack({
  animation: 'attack_horizontal',
  // Weapon comes from submeshes - no need for weaponAssetPath
  impactFX: fromFXPreset('melee_hit'),
});

const DANIEL_PROJECTILE_THROW_ATTACK = createRangedProjectileAttack({
  animation: 'attack_throw',
  maskBones: [...UPPER_BODY_MASK],
  // Configure projectile handover timing explicitly for this animation.
  events: [{ time: DANIEL_PROJECTILE_HANDOVER_TIME, type: 'detachProjectile' }],
  impactFX: fromFXPreset('projectile_hit'),
  projectile: {
    assetId: 'bass_projectile',
    assetPath: '#bass',
    scale: 0.8,
    type: 'kinematic',
    speed: 15,
    directionMode: 'facing',
    maxLifetime: 5,
    muzzleBone: 'mixamorig:RightHand',
  },
});

export const DANIEL_CONFIG = createCharacter({
  id: 'daniel',
  name: 'Daniel',
  assetPath: '/assets/characters/daniel3/Idle-blender.glb',
  scale: 1.0,
  renderFixes: {
    doubleSidedMaterials: true,
    transparentDepthPrePass: true
  },
  modelAxes: {
    forward: { x: 0, y: 0, z: -1 }
  },
  moveSpeed: RUN_SPEED,
  physics: {
    jumpHeight: 1.4
  },
  pathConstraint: {
    enabled: true,
    stiffness: 100,  // Spring force coefficient (N/m)
    damping: 10,     // Damping coefficient
    constrainVertical: false,  // Only constrain XZ (horizontal) movement
    attractionGain: 40.0        // Stronger pull to stay on path
  },
  turnMode: 'interpolate',
  animations: {
    idle: createIdleAnimation('/assets/characters/daniel3/Idle.glb'),
    run: createRunAnimation('/assets/characters/daniel3/Running.glb'),
    jump: createJumpAnimation('/assets/characters/daniel3/Jumping.glb', {
      exitTime: { enabled: false, time: 0 } // Allow instant exit to attack
    }),
    fall: createIdleAnimation('/assets/characters/daniel3/Falling Idle.glb', {
      exitTime: { enabled: false, time: 0 } // Allow instant exit to attack
    }),
    turn: createRunAnimation('/assets/characters/daniel3/Walking Turn 180.glb', {
      lockFacing: true,
      speed: 2,
      applyRootMotion: true,
      exitTime: { enabled: false, time: 0 } // Allow instant exit to attack
    }),
    attack_horizontal: createAttackAnimation('/assets/characters/daniel3/Standing Melee Attack 360 Low.glb', {
      applyRootMotion: true, // Re-enable root motion for attack
      exitTime: { enabled: true, time: 0.4 }, // Allow early exit for fast attacks
      speed: 4 // Faster animation for quick attack chaining
    }),
    attack_throw: createAttackAnimation('/assets/characters/daniel3/Throw Object.glb', {
      applyRootMotion: true, // Re-enable root motion for attack
      mask: [...UPPER_BODY_MASK],
      exitTime: { enabled: true, time: 0.9 }, // Allow early exit for fast attacks
      speed: 4, // Faster animation for quick attack chaining
      startOffset: DANIEL_THROW_ANIM_START_OFFSET,
    }),
    hit_react: createIdleAnimation('/assets/characters/daniel3/Head Hit.glb', {
      speed: 2,
      phases: {
        windup: { start: 0.0, end: 0.2 },
        active: { start: 0.2, end: 0.55 },
        recovery: { start: 0.55, end: 1.0 }
      }
    }),
    death: createIdleAnimation('/assets/characters/hotdog/Zombie Dying.glb', {
      speed: 4,
      applyRootMotion: true
    })
  },
  transitions: [
    { from: 'idle', to: 'turn', duration: 0.1 },
    { from: 'run', to: 'turn', duration: 0.15 },
    { from: 'turn', to: 'run', duration: 0.2 },
    { from: 'turn', to: 'idle', duration: 0.15 },
    { from: 'land', to: 'run', duration: 0.15, blendRatio: 0.3 },
    { from: 'land', to: 'idle', duration: 0.2 },
    { from: 'run', to: 'idle', duration: 0.2 },
    { from: 'idle', to: 'run', duration: 0.3 },
    { from: 'idle', to: 'attack_horizontal', duration: 0.1 },
    { from: 'run', to: 'attack_horizontal', duration: 0.1 },
    { from: 'turn', to: 'attack_horizontal', duration: 0.1 },
    { from: 'jump', to: 'attack_horizontal', duration: 0.1 },
    { from: 'fall', to: 'attack_horizontal', duration: 0.1 },
    { from: 'attack_horizontal', to: 'idle', duration: 0.2 }
  ],
  combatBindings: {
    action: 'melee_horizontal',
    action2: 'projectile_throw',
    action3: 'projectile_throw'
  },
  combatAttacks: {
    melee_horizontal: DANIEL_MELEE_HORIZONTAL_ATTACK,
    projectile_throw: DANIEL_PROJECTILE_THROW_ATTACK,
  },
  submeshes: [
    {
      id: 'weapon',
      source: '#bass', // Embedded entity from character GLB
      // attachBone comes from hm_attach_bone in entity extras
      scale: 1.0
    }
  ],

  onDamageReceived: ({ baseDamage, source }) => (source === 'projectile' ? baseDamage * 0.8 : baseDamage),
  lifecycle: createBasicLifecycleScript({
    id: 'hotdog-default-lifecycle',
    hitAnimation: 'hit_react',
    deathAnimation: 'death',
    despawnMode: 'after-animation'
  })
});